import 'zone.js';
import 'zone.js/testing';
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ApplicationRef, ChangeDetectorRef, OnChanges, SimpleChanges, ViewChildren, QueryList, AfterViewInit
} from '@angular/core';
import {
  CdkDragDrop,
  CdkDragEnter,
  CdkDragExit,
  CdkDragMove,
  CdkDragStart, CdkDropList,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import {ITdoImageGridItem} from './models/i-tdo-image-grid-item';
import {FileManagerService} from '../../../modules/file-manager/file-manager.service';
import {DocumentDataImageModel} from '../../../main-interface/controls/tdo-organizer/models/document-data-image.model';
import {DomSanitizer} from '@angular/platform-browser';
import {tick} from '@angular/core/testing';
import {TdoImageGridContextModel} from './models/tdo-image-grid-context.model';
import {CollageItemModel} from '../../../modules/imaging/image-collage/models/collage-item.model';
import {DocumentInFolderDataModel} from '../../../models/document/document-in-folder-data.model';
import {LabelResponseModel} from '../../../models/labels/label-response.model';
import { MatCheckboxChange } from '@angular/material/checkbox';

export class MovedGridItem {
  index: number;
  model: ITdoImageGridItem;
}

@Component({
  selector: 'app-tdo-image-grid',
  templateUrl: './tdo-image-grid.component.html',
  styleUrls: ['./tdo-image-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TdoImageGridComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('rowLayout') rowLayout: ElementRef;
  @ViewChildren('gridRow') rows: QueryList<ElementRef>;

  @Input() gridContainer: HTMLDivElement;
  @Input() dataSet: ITdoImageGridItem[] = [];
  @Input() margin = 8;
  @Input() boxWidth = 150;
  @Input() isDragEnabled = true;
  @Input() isExternalDragEnabled = true;
  @Input() isDocumentManagerMode = true;
  @Input() isZoomEnabled = false;

  @Output() itemsEntered = new EventEmitter<CdkDragEnter<ITdoImageGridItem>>();
  @Output() itemsExited = new EventEmitter<CdkDragExit<ITdoImageGridItem>>();
  @Output() itemsDropped = new EventEmitter<CdkDragDrop<ITdoImageGridItem>>();
  @Output() itemsStarted = new EventEmitter<CdkDragStart<ITdoImageGridItem>>();
  @Output() itemsSelected = new EventEmitter<ITdoImageGridItem[]>();
  @Output() itemsSorted = new EventEmitter<ITdoImageGridItem[]>();
  @Output() itemDoubleClick = new EventEmitter<ITdoImageGridItem>();
  @Output() contextOpened = new EventEmitter<TdoImageGridContextModel>();
  @Output() backgroundContextOpen = new EventEmitter<MouseEvent>();
  @Output() filesDraggedIntoBrowser = new EventEmitter<FileList>();

  // one dimensional input model
  // items: Array<number> = Array.from({ length: 21 }, (v, k) => k + 1);
  // two dimensional table matrix representing view model
  @Input() imageLabels: LabelResponseModel[] = [];
  @Input() imageTooltips: LabelResponseModel[] = [];
  @Input() disabledIDs: LabelResponseModel[] = [];
  @Input() checkedIDsClaimAttachmentAdded: LabelResponseModel[] = [];
  @Input() checkedIDsClaimAttachmentSent: LabelResponseModel[] = [];
  itemsTable: Array<ITdoImageGridItem[]>;
  columnSize = -150;
  isGridDropList = true;
  isForceUpdate = false;

  sanitizedUrl = '';
  isLoading = false;
  selectedItemsList: ITdoImageGridItem[] = [];
  zoomLeft: number;
  zoomTop: number;
  bounds: DOMRect;
  dragX: number;
  dragY: number;
  isControlDown = false;
  descriptionHeight = 30;

  private mouseOverItem: ITdoImageGridItem;

  @HostListener('window:keydown', ['$event'])
  onKeyDown($event: KeyboardEvent) {
    if ($event.ctrlKey) {
      this.isControlDown = true;
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp($event: KeyboardEvent) {
    this.isControlDown = false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    setTimeout(() => {
      this.forceUpdate(this.dataSet);
    }, 200);
  }

  @HostListener('click', ['$event'])
  onGlobalClick(event) {
    this.deselectAll();
  }

  constructor(private fileManagerService: FileManagerService, private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.bounds = this.gridContainer.getBoundingClientRect();
    let { width } = this.gridContainer.getBoundingClientRect();
    if (width < 500) {
      width = 500;
    }
    const columnSize = Math.floor(width / (this.boxWidth + (this.margin * 2)));
    this.columnSize = columnSize;
    this.forceUpdate(this.dataSet);
  }

  ngAfterViewInit() {
    const ivl = setInterval(() => {
      if (this.rows && this.rows.length > 0) {
        clearInterval(ivl);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.boxWidth) {
      setTimeout(() => {
        this.bounds = this.gridContainer.getBoundingClientRect();
        let { width } = this.gridContainer.getBoundingClientRect();
        if (width < 500) {
          width = 500;
        }
        this.forceUpdate(this.dataSet);
      }, 10);
    }

    if (changes.dataSet) {
      this.dataSet = this.dataSet;
      // claim attachments added
      if (this.checkedIDsClaimAttachmentAdded.length > 0) {
        this.checkedIDsClaimAttachmentAdded.forEach(a => {
          this.dataSet.forEach(b => {
            if (a.objectId == b.model.DocumentID) {
              this.selectedItemsList.push(b);
            }
          });
        });
      }

      // initially emit any preselected items
      this.itemsSelected.emit(this.selectedItemsList);
    }
  }

  forceUpdate(dataSet: ITdoImageGridItem[]) {
    this.dataSet = dataSet;
    this.columnSize = -1;
    this.isForceUpdate = true;
    this.getItemsTable(null);
    this.initTable();
    // tick();
    this.cdr.detectChanges();
  }

  getItemsTable(rowLayout: Element): ITdoImageGridItem[][] {
    // calculate column size per row
    let { width } = this.gridContainer.getBoundingClientRect();
    if (width < 500) {
      width = 500;
    }

    const columnSize = Math.floor(width / (this.boxWidth + (this.margin * 2) + 7));

    // view has been resized? => update table with new column size
    if (columnSize !== this.columnSize || this.isForceUpdate) {
      if (this.isForceUpdate) {
        this.isForceUpdate = false;
      }
    }
    this.columnSize = columnSize;
    return this.itemsTable;
  }

  initTable(): boolean {
    if (!this.dataSet) {
      return false;
    }
    // create table rows based on input list
    // example: [1,2,3,4,5,6] => [ [1,2,3], [4,5,6] ]
    this.itemsTable = this.dataSet
      .filter((_, outerIndex) => outerIndex % this.columnSize === 0) // create outer list of rows
      .map((
          _,
          rowIndex // fill each row from...
        ) =>
          this.dataSet.slice(
            rowIndex * this.columnSize, // ... row start and
            rowIndex * this.columnSize + this.columnSize // ...row end
          )
      );
    return true;
  }

  reorderDroppedItem(event: CdkDragDrop<ITdoImageGridItem[]>): void {
    // For multi
    // - Determine if first item is moved to new array

    if (event.item.data.length === 1) {
      if (event.previousContainer === event.container) {
        moveItemInArray(
          event.container.data,
          event.previousIndex,
          event.currentIndex
        );
      } else {
        // different rows? => transfer item from one to another list
        transferArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex
        );
      }

      // update items after drop: flatten matrix into list
      // example: [ [1,2,3], [4,5,6] ] => [1,2,3,4,5,6]
      this.dataSet = this.itemsTable.reduce(
        (previous, current) => previous.concat(current),
        []
      );
    } else {
      // Determine which container index the new one is.
      const rows = this.rows.toArray();
      const dropRowIndex = rows.findIndex(p => p.nativeElement === event.container.element.nativeElement);
      let newArr: Array<ITdoImageGridItem> = new Array<ITdoImageGridItem>();
      const movedItems: MovedGridItem[] = [];
      const skippedItems: ITdoImageGridItem[] = [];

      const index = (dropRowIndex * this.columnSize) + event.currentIndex;

      if (event.currentIndex < event.previousIndex) {
        for (let i = 0; i < index; i++) {
          const item = this.dataSet[i];
          if (!event.item.data.includes(item)) {
            const model = this.dataSet[i];
            newArr.push(model);
          }
        }
      } else if (event.currentIndex > event.previousIndex) {
        for (let i = 0; i <= index; i++) {
          const item = this.dataSet[i];
          if (!event.item.data.includes(item)) {
            const model = this.dataSet[i];
            newArr.push(model);
          }
        }
      }

      for (const model of event.item.data) {
        newArr.push(model);
      }

      for (let i = 0; i < this.dataSet.length; i++) {
        const item = this.dataSet[i];
        if (!event.item.data.includes(item) && !newArr.includes(item)) {
          newArr.push(item);
        }
      }

      this.dataSet = newArr;
    }

    this.itemsSorted.emit(this.dataSet);
    this.initTable();
  }

  dragMoved(event): void {
    // console.log('MOVED');
  }

  dragEntered(event): void {

  }

  /*  entered(event: CdkDragEnter): void {
      moveItemInArray(this.dataSet, event.item.data, event.container.data);
    }*/

  onItemDragDropped(event: CdkDragDrop<ITdoImageGridItem>): void {
    this.itemsDropped.emit(event);
  }

  onItemDragStarted(event: CdkDragStart<ITdoImageGridItem>): void {
    this.itemsStarted.emit(event);
  }

  onItemDragEntered(event: CdkDragEnter<ITdoImageGridItem>): void {
    if (event.container.id.includes('tdo-grid-drop-list')) {
      this.isGridDropList = true;
    } else {
      this.isGridDropList = false;
    }

    this.itemsEntered.emit(event);
  }

  onItemDragExited(event: CdkDragExit<ITdoImageGridItem>): void {
    this.itemsExited.emit(event);
  }

  getDocumentManagerUrl(item: ITdoImageGridItem) {
    const mod = item as DocumentDataImageModel;
    return this.fileManagerService.getDocumentUrlFromDocument(mod.model);
  }

  onDoubleClick(event: MouseEvent, item: ITdoImageGridItem) {
    this.itemDoubleClick.emit(item);
  }


  onCheckboxChange($event: MatCheckboxChange, item: ITdoImageGridItem) {
    event.stopImmediatePropagation();
    if ($event.checked) {
      if (!this.selectedItemsList.includes(item)) {
        this.selectedItemsList.push(item);
      }
    }
    else {
      if (this.selectedItemsList.includes(item)) {
        const idx = this.selectedItemsList.findIndex(p => p === item);
        if (idx > -1) {
          this.selectedItemsList.splice(idx, 1);
        }
      }
    }
    this.itemsSelected.emit(this.selectedItemsList);
  }

  onClick(event: MouseEvent, item: ITdoImageGridItem, row: HTMLDivElement) {
    item.row = row;
    event.stopImmediatePropagation();
    if (event.ctrlKey) {
      if (this.selectedItemsList.includes(item)) {
        const idx = this.selectedItemsList.findIndex(p => p === item);
        if (idx > -1) {
          this.selectedItemsList.splice(idx, 1);
        }
      } else {
        this.selectedItemsList.push(item);
      }
    } else if (event.shiftKey && this.selectedItemsList.length > 0) {
      const startIdx = this.dataSet.findIndex(p => p === this.selectedItemsList[0]);
      const endIdx = this.dataSet.findIndex(p => p === item);
      let isForward = true;
      if (startIdx > endIdx) {
        isForward = false;
      }
      if (isForward) {
        for (let i = startIdx; i < endIdx + 1; i++) {
          if (!this.selectedItemsList.includes(this.dataSet[i])) {
            this.selectedItemsList.push(this.dataSet[i]);
          }
        }
      } else {
        for (let i = startIdx; i > endIdx - 1; i--) {
          if (!this.selectedItemsList.includes(this.dataSet[i])) {
            this.selectedItemsList.push(this.dataSet[i]);
          }
        }
      }
    } else {
      if (!this.getDisabled(item.model.dataDocument.DocumentID)) {
        this.selectedItemsList = [item];
      }
    }

    this.itemsSelected.emit(this.selectedItemsList);
  }

  isItemSelected(item: ITdoImageGridItem): boolean {
    let returnVal: boolean = false;

    if (this.selectedItemsList.includes(item)) {
      returnVal = true;
    }

    if (this.checkedIDsClaimAttachmentSent) {
      this.checkedIDsClaimAttachmentSent.forEach(a => {
        if (a.objectId == item.model.DocumentID) {
          returnVal = true;
        }
      });
    }

    return returnVal;
  }

  onContextMenu(event: MouseEvent, item: ITdoImageGridItem) {
    event.stopImmediatePropagation();
    const model = new TdoImageGridContextModel();
    model.event = event;
    model.contextItem = item;
    model.selectedItems = this.selectedItemsList;
    this.contextOpened.emit(model);
  }

  deselectAll() {
    this.selectedItemsList = [];
    this.itemsSelected.emit(this.selectedItemsList);
  }

  getItems(item: ITdoImageGridItem) {
    const selItems = this.selectedItemsList.concat();
    if (!selItems.includes(item)) {
      selItems.push(item);
    }
    return selItems;
  }

  onMouseOver(event: MouseEvent, item: ITdoImageGridItem, viewItem: HTMLDivElement) {
    this.zoomLeft = viewItem.clientLeft;
    this.zoomTop = viewItem.clientTop;
    const xOnPage = viewItem.offsetLeft + this.bounds.left;
    const yOnPage = viewItem.scrollTop + this.bounds.top;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const r = xOnPage + 430;
    const ry = yOnPage + 430;
    if (r > windowWidth) {
      this.zoomLeft = windowWidth - r;
    }
    if (ry > windowHeight) {
      this.zoomTop = windowHeight - ry;
    }
    this.mouseOverItem = item;
  }

  isMouseOver(item: ITdoImageGridItem) {
    return item === this.mouseOverItem;
  }

  onMouseOut($event: MouseEvent, item: ITdoImageGridItem) {
    this.mouseOverItem = null;
  }

  onDeadSpaceClick(event: MouseEvent) {
    // this.deselectAll();
  }

  onDeadSpaceContext(event: MouseEvent) {
    this.backgroundContextOpen.emit(event);
  }

  onFileDragToBrowser(event: FileList) {
    this.filesDraggedIntoBrowser.emit(event);
  }

  shouldDisableDrag() {
    return this.isControlDown;
  }

  onItemDragMoved(event: CdkDragMove<ITdoImageGridItem[]>) {
    this.dragX = event.pointerPosition.x;
    this.dragY = event.pointerPosition.y;
  }

  forceResort() {
    this.itemsSorted.emit(this.dataSet);
  }

  getImageLabel(DocumentID: string) {
    const description = this.imageLabels.find(p => p.objectId === DocumentID);
    if (description) {
      return description.contentHtml;
    } else {
      return '';
    }
  }


  getImageLabelTooltip(DocumentID: string) {
    const description = this.imageLabels.find(p => p.objectId === DocumentID);
    if (description) {
      return description.contentHtml.replaceAll('<br/>', '\n\r');
    } else {
      return '';
    }
  }

  getImageTooltip(DocumentID: string) {
    const description = this.imageTooltips.find(p => p.objectId === DocumentID);
    if (description) {
      return description.contentHtml.replaceAll('<br/>', '\n\r');
    } else {
      return '';
    }
  }
  getDisabled(DocumentID: string) {
    const dis = this.disabledIDs.find(p => p.objectId === DocumentID);
    if (dis) {
      return true;
    } else {
      return false;
    }
  }
}
